import React, { useState, useMemo, useRef, useCallback } from "react";
import { Bookmark, MapPin, Search } from "lucide-react";
import {
  Heading3,
  Heading5,
  NormalParagraph,
  SmallText,
  XsmallText,
} from "../../lib/ui/components/typography/Typography";
import Modal from "../../lib/ui/components/Modal";
import Button from "../../lib/ui/components/button/Button";

const INDUSTRIES = [
  "All Industries",
  "Technology",
  "Finance",
  "Healthcare",
  "Manufacturing",
  "Media",
  "Consulting",
];

const SENIORITY_LEVELS = [
  "All Levels",
  "Junior",
  "Mid-Level",
  "Senior",
  "Lead",
  "Staff",
  "Principal",
];

const LOCATIONS = [
  "All Locations",
  "San Francisco, CA",
  "New York, NY",
  "Austin, TX",
  "Seattle, WA",
  "Boston, MA",
  "Chicago, IL",
  "Remote (US)",
];

const WORK_ARRANGEMENTS = [
  "Any Arrangement",
  "Remote",
  "Hybrid",
  "On-Site",
];

const MOCK_DESCRIPTION = `About the Role

We are looking for a Senior Network Administrator to join our growing infrastructure team. In this role you will be responsible for the design, implementation, and ongoing management of our enterprise network spanning multiple data centers, cloud regions, and office locations. You will work cross-functionally with engineering, security, and IT operations to ensure reliable, high-performance connectivity for all internal and customer-facing services.

This is a high-impact position with visibility across the entire organization. You will help define our network strategy, evaluate emerging technologies, and lead initiatives that directly improve uptime, throughput, and security posture.

What You'll Do

• Design, deploy, and maintain LAN, WAN, and SD-WAN architectures across our hybrid cloud environment including AWS, Azure, and on-premises data centers.
• Configure and manage routers, switches, firewalls, load balancers, and VPN concentrators from vendors such as Cisco, Palo Alto Networks, and Arista.
• Monitor network performance using tools like Datadog, ThousandEyes, and Wireshark; proactively identify bottlenecks and resolve incidents before they affect end users.
• Develop and enforce network security policies, including segmentation, zero-trust principles, ACLs, and intrusion detection/prevention systems.
• Automate routine network tasks using Python, Ansible, or Terraform to reduce toil and improve consistency across environments.
• Collaborate with the DevOps and SRE teams to integrate networking into CI/CD pipelines and infrastructure-as-code workflows.
• Participate in on-call rotations and lead incident response for network-related outages, performing root cause analysis and publishing post-mortems.
• Maintain comprehensive documentation of network topology, runbooks, and change management procedures.
• Mentor junior network engineers and contribute to hiring efforts as the team scales.

What We're Looking For

• 5+ years of professional experience in network engineering or administration.
• Deep hands-on expertise with TCP/IP, DNS, DHCP, BGP, OSPF, MPLS, and VXLAN.
• Experience operating in large-scale environments (10,000+ endpoints or multi-region cloud deployments).
• Proficiency with at least one infrastructure-as-code tool (Terraform, Pulumi, CloudFormation).
• Strong scripting skills in Python, Bash, or similar languages for automation.
• Familiarity with container networking (Kubernetes CNI, service mesh, Envoy).
• Excellent troubleshooting and communication skills; ability to translate complex technical issues for non-technical stakeholders.
• One or more relevant certifications preferred: CCNP, CCIE, PCNSE, AWS Advanced Networking, or equivalent.

Nice to Have

• Experience with network observability platforms (Kentik, NetFlow, sFlow).
• Background in compliance frameworks such as SOC 2, ISO 27001, or HIPAA as they relate to network controls.
• Contributions to open-source networking projects or community forums.
• Prior experience at a high-growth startup or during a significant infrastructure migration.

Compensation & Benefits

• Competitive base salary with annual performance bonuses.
• Equity package (stock options) with a four-year vesting schedule.
• Comprehensive health, dental, and vision insurance for you and your dependents.
• 401(k) plan with company match up to 4%.
• Flexible PTO policy plus company-wide recharge days.
• Annual learning & development stipend ($2,500) for conferences, courses, and certifications.
• Home office setup allowance for remote and hybrid employees.
• Monthly wellness reimbursement covering gym memberships, mental health apps, and more.

Our Culture

We believe great infrastructure is invisible—when it works, nobody notices, and that is the highest compliment. Our team values curiosity, ownership, and collaboration. We run blameless post-mortems, invest heavily in automation, and celebrate wins both big and small. Whether you are debugging a fiber cut at 2 AM or presenting a capacity plan to leadership, you will be supported by a team that genuinely has your back.

We are committed to building a diverse and inclusive workplace. We encourage applications from candidates of all backgrounds and experience levels. If you are excited about this role but don't meet every single requirement, we still encourage you to apply—you might be exactly the person we are looking for.`;

const MOCK_ROLES = [
  {
    id: 1,
    logo: "A",
    companyName: "Acme Corp",
    role: "Senior Network Administrator",
    completionPercent: 92,
    industry: "Technology",
    seniority: "Senior",
    location: "San Francisco, CA",
    workArrangement: "Hybrid",
    description: MOCK_DESCRIPTION,
  },
  {
    id: 2,
    logo: "G",
    companyName: "Globex Inc",
    role: "Cloud Infrastructure Engineer",
    completionPercent: 81,
    industry: "Technology",
    seniority: "Mid-Level",
    location: "Austin, TX",
    workArrangement: "Remote",
    description: MOCK_DESCRIPTION,
  },
  {
    id: 3,
    logo: "I",
    companyName: "Initech",
    role: "Product Designer",
    completionPercent: 67,
    industry: "Consulting",
    seniority: "Senior",
    location: "New York, NY",
    workArrangement: "On-Site",
    description: MOCK_DESCRIPTION,
  },
  {
    id: 4,
    logo: "W",
    companyName: "Wayne Enterprises",
    role: "Security Analyst",
    completionPercent: 88,
    industry: "Finance",
    seniority: "Mid-Level",
    location: "Chicago, IL",
    workArrangement: "Hybrid",
    description: MOCK_DESCRIPTION,
  },
  {
    id: 5,
    logo: "S",
    companyName: "Stark Industries",
    role: "Machine Learning Engineer",
    completionPercent: 74,
    industry: "Technology",
    seniority: "Lead",
    location: "Seattle, WA",
    workArrangement: "Remote",
    description: MOCK_DESCRIPTION,
  },
  {
    id: 6,
    logo: "O",
    companyName: "Oscorp",
    role: "Full Stack Developer",
    completionPercent: 56,
    industry: "Healthcare",
    seniority: "Junior",
    location: "Boston, MA",
    workArrangement: "On-Site",
    description: MOCK_DESCRIPTION,
  },
  {
    id: 7,
    logo: "U",
    companyName: "Umbrella Tech",
    role: "Platform Engineer",
    completionPercent: 95,
    industry: "Technology",
    seniority: "Staff",
    location: "Remote (US)",
    workArrangement: "Remote",
    description: MOCK_DESCRIPTION,
  },
  {
    id: 8,
    logo: "V",
    companyName: "Vought International",
    role: "Data Analyst",
    completionPercent: 43,
    industry: "Media",
    seniority: "Junior",
    location: "New York, NY",
    workArrangement: "Hybrid",
    description: MOCK_DESCRIPTION,
  },
  {
    id: 9,
    logo: "L",
    companyName: "LexCorp",
    role: "DevOps Engineer",
    completionPercent: 79,
    industry: "Finance",
    seniority: "Senior",
    location: "San Francisco, CA",
    workArrangement: "Remote",
    description: MOCK_DESCRIPTION,
  },
  {
    id: 10,
    logo: "C",
    companyName: "Cyberdyne Systems",
    role: "Systems Engineer",
    completionPercent: 61,
    industry: "Manufacturing",
    seniority: "Mid-Level",
    location: "Austin, TX",
    workArrangement: "On-Site",
    description: MOCK_DESCRIPTION,
  },
  {
    id: 11,
    logo: "M",
    companyName: "Massive Dynamic",
    role: "Principal Architect",
    completionPercent: 98,
    industry: "Consulting",
    seniority: "Principal",
    location: "Boston, MA",
    workArrangement: "Hybrid",
    description: MOCK_DESCRIPTION,
  },
  {
    id: 12,
    logo: "P",
    companyName: "Pied Piper",
    role: "Frontend Engineer",
    completionPercent: 85,
    industry: "Technology",
    seniority: "Mid-Level",
    location: "San Francisco, CA",
    workArrangement: "Remote",
    description: MOCK_DESCRIPTION,
  },
];

function FilterSelect({ label, value, onChange, options }) {
  return (
    <div className="flex flex-col gap-1">
      <XsmallText className="text-gray-500 font-medium">{label}</XsmallText>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-small text-gray-700 shadow-sm focus:border-[color:var(--primary-400)] focus:ring-1 focus:ring-[color:var(--primary-400)] focus:outline-none transition-colors"
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

function CompletionRangeFilter({
  minCompletion,
  maxCompletion,
  onMinChange,
  onMaxChange,
}) {
  const trackRef = useRef(null);

  const pctFromEvent = useCallback((e) => {
    const rect = trackRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    return Math.round(Math.max(0, Math.min(100, (x / rect.width) * 100)));
  }, []);

  const startDrag = useCallback(
    (which) => (e) => {
      e.preventDefault();
      const isTouch = e.type === "touchstart";

      const onMove = (ev) => {
        const pct = pctFromEvent(ev);
        if (which === "min") onMinChange(pct);
        else onMaxChange(pct);
      };

      const onUp = () => {
        if (isTouch) {
          document.removeEventListener("touchmove", onMove);
          document.removeEventListener("touchend", onUp);
        } else {
          document.removeEventListener("mousemove", onMove);
          document.removeEventListener("mouseup", onUp);
        }
      };

      if (isTouch) {
        document.addEventListener("touchmove", onMove);
        document.addEventListener("touchend", onUp);
      } else {
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
      }
    },
    [pctFromEvent, onMinChange, onMaxChange]
  );

  const activeTrackLeft = `${minCompletion}%`;
  const activeTrackWidth = `${Math.max(0, maxCompletion - minCompletion)}%`;

  return (
    <div className="min-w-[220px] flex-1">
      <div className="flex items-center justify-between mb-1">
        <XsmallText className="text-gray-500 font-medium">Interview Completion</XsmallText>
        <XsmallText className="text-gray-500">
          {minCompletion}% - {maxCompletion}%
        </XsmallText>
      </div>

      <div ref={trackRef} className="relative h-9 flex items-center select-none">
        <div className="absolute left-0 right-0 h-1.5 rounded-full bg-gray-200" />
        <div
          className="absolute h-1.5 rounded-full"
          style={{
            left: activeTrackLeft,
            width: activeTrackWidth,
            backgroundColor: "var(--primary-main)",
          }}
        />

        <span
          role="slider"
          aria-label="Minimum interview completion"
          aria-valuenow={minCompletion}
          aria-valuemin={0}
          aria-valuemax={100}
          tabIndex={0}
          onMouseDown={startDrag("min")}
          onTouchStart={startDrag("min")}
          className="absolute -translate-x-1/2 h-3.5 w-3.5 rounded-full border-2 border-white shadow-sm cursor-pointer"
          style={{ left: `${minCompletion}%`, backgroundColor: "var(--primary-main)" }}
        />
        <span
          role="slider"
          aria-label="Maximum interview completion"
          aria-valuenow={maxCompletion}
          aria-valuemin={0}
          aria-valuemax={100}
          tabIndex={0}
          onMouseDown={startDrag("max")}
          onTouchStart={startDrag("max")}
          className="absolute -translate-x-1/2 h-3.5 w-3.5 rounded-full border-2 border-white shadow-sm cursor-pointer"
          style={{ left: `${maxCompletion}%`, backgroundColor: "var(--primary-main)" }}
        />
      </div>
    </div>
  );
}

function RoleCard({ role, onSelect }) {
  const pct = Math.max(0, Math.min(100, role.completionPercent));
  const barColor = pct > 80 ? "#16a34a" : "var(--primary-main)";

  return (
    <div
      onClick={() => onSelect(role)}
      className="border border-gray-200 rounded-xl bg-white p-5 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer flex flex-col"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center">
          <span className="text-small font-bold text-gray-600">{role.logo}</span>
        </div>
        <div className="min-w-0 flex-1">
          <SmallText weight="bold" className="text-gray-900 leading-snug">
            {role.role}
          </SmallText>
          <XsmallText className="text-gray-500 mt-0.5">{role.companyName}</XsmallText>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-body font-medium text-gray-600">
          {role.industry}
        </span>
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-body font-medium text-gray-600">
          {role.seniority}
        </span>
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-body font-medium text-gray-600">
          {role.workArrangement}
        </span>
      </div>

      <XsmallText className="text-gray-500 mb-3 truncate" title={role.location}>
        {role.location}
      </XsmallText>

      <div className="mt-auto pt-3 border-t border-gray-100">
        <div className="flex items-center justify-between mb-1">
          <XsmallText className="text-gray-500">Interview Completion</XsmallText>
          <XsmallText className="text-gray-500 font-semibold">{pct}%</XsmallText>
        </div>
        <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${pct}%`, backgroundColor: barColor }}
          />
        </div>
      </div>
    </div>
  );
}

function RoleDetailModal({ role, show, onClose, saved, onToggleSave }) {
  if (!role) return null;

  const pct = Math.max(0, Math.min(100, role.completionPercent));
  const remainingPct = Math.max(0, 100 - pct);
  const barColor = pct > 80 ? "#16a34a" : "var(--primary-main)";

  return (
    <Modal show={show} onClose={onClose} size="4xl" dismissible>
      <Modal.Header>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center">
            <span className="text-small font-bold text-gray-600">{role.logo}</span>
          </div>
          <div>
            <Heading5 className="!text-gray-900 !mb-0">{role.role}</Heading5>
            <SmallText className="text-gray-500">{role.companyName}</SmallText>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="space-y-5">
          {/* Location */}
          <div className="flex items-center gap-2 text-gray-500">
            <MapPin className="h-4 w-4 flex-shrink-0" aria-hidden />
            <SmallText className="text-gray-600">{role.location}</SmallText>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-body font-medium text-gray-600">
              {role.industry}
            </span>
            <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-body font-medium text-gray-600">
              {role.seniority}
            </span>
            <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-body font-medium text-gray-600">
              {role.workArrangement}
            </span>
          </div>

          {/* Interview completion */}
          <div className="rounded-lg bg-gray-50 p-4">
            <div className="flex items-center justify-between mb-2">
              <SmallText className="text-gray-600 font-medium">Interview Completion</SmallText>
              <SmallText className="font-semibold" style={{ color: barColor }}>{pct}%</SmallText>
            </div>
            <div className="h-2.5 w-full rounded-full bg-gray-200 overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${pct}%`, backgroundColor: barColor }}
              />
            </div>
            <div className="mt-3 space-y-1">
              <SmallText weight="bold" className="text-gray-700">
                You are almost there, keep going.
              </SmallText>
              <XsmallText className="text-gray-500">
                You have already answered most of this role's interview questions through previous interviews, so you only have about {remainingPct}% left to complete.
              </XsmallText>
            </div>
          </div>

          {/* Job description */}
          <div>
            <SmallText weight="bold" className="text-gray-900 mb-2 block">
              Job Description
            </SmallText>
            <div className="max-h-96 overflow-y-auto rounded-lg border border-gray-100 bg-gray-50/50 p-4 text-small text-gray-600 leading-relaxed whitespace-pre-line">
              {role.description}
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex items-center justify-between w-full">
          <Button
            variant="secondary"
            onClick={() => onToggleSave(role.id)}
            leftIcon={
              <Bookmark className="h-4 w-4" fill={saved ? "currentColor" : "none"} aria-hidden />
            }
          >
            {saved ? "Saved" : "Save Role"}
          </Button>
          <Button variant="primary" onClick={onClose}>
            Complete Interview
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default function CandidateCompanies() {
  const [search, setSearch] = useState("");
  const [industry, setIndustry] = useState(INDUSTRIES[0]);
  const [seniority, setSeniority] = useState(SENIORITY_LEVELS[0]);
  const [location, setLocation] = useState(LOCATIONS[0]);
  const [workArrangement, setWorkArrangement] = useState(WORK_ARRANGEMENTS[0]);
  const [minCompletion, setMinCompletion] = useState(0);
  const [maxCompletion, setMaxCompletion] = useState(100);
  const [selectedRole, setSelectedRole] = useState(null);
  const [savedRoles, setSavedRoles] = useState(new Set());

  const handleToggleSave = useCallback((roleId) => {
    setSavedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  }, []);

  const handleMinCompletionChange = (value) => {
    setMinCompletion(Math.min(value, maxCompletion));
  };

  const handleMaxCompletionChange = (value) => {
    setMaxCompletion(Math.max(value, minCompletion));
  };

  const filteredRoles = useMemo(() => {
    const q = search.toLowerCase().trim();

    return MOCK_ROLES.filter((r) => {
      if (q && !r.role.toLowerCase().includes(q) && !r.companyName.toLowerCase().includes(q)) {
        return false;
      }
      if (industry !== INDUSTRIES[0] && r.industry !== industry) return false;
      if (seniority !== SENIORITY_LEVELS[0] && r.seniority !== seniority) return false;
      if (location !== LOCATIONS[0] && r.location !== location) return false;
      if (workArrangement !== WORK_ARRANGEMENTS[0] && r.workArrangement !== workArrangement)
        return false;
      if (r.completionPercent < minCompletion || r.completionPercent > maxCompletion)
        return false;
      return true;
    }).sort((a, b) => b.completionPercent - a.completionPercent);
  }, [search, industry, seniority, location, workArrangement, minCompletion, maxCompletion]);

  const activeFilterCount =
    [industry, seniority, location, workArrangement].filter(
      (v, i) => v !== [INDUSTRIES, SENIORITY_LEVELS, LOCATIONS, WORK_ARRANGEMENTS][i][0]
    ).length + (minCompletion !== 0 || maxCompletion !== 100 ? 1 : 0);

  const clearFilters = () => {
    setSearch("");
    setIndustry(INDUSTRIES[0]);
    setSeniority(SENIORITY_LEVELS[0]);
    setLocation(LOCATIONS[0]);
    setWorkArrangement(WORK_ARRANGEMENTS[0]);
    setMinCompletion(0);
    setMaxCompletion(100);
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <div className="mb-6">
        <Heading3 className="mb-2 !text-[color:var(--primary-900)]">Roles</Heading3>
        <NormalParagraph className="text-gray-500">
          Browse matching roles sorted by how closely your interviews align.
        </NormalParagraph>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none"
            aria-hidden
          />
          <input
            type="text"
            placeholder="Search by role or company..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-200 bg-white pl-10 pr-4 py-2.5 text-small text-gray-700 shadow-sm placeholder:text-gray-400 focus:border-[color:var(--primary-400)] focus:ring-1 focus:ring-[color:var(--primary-400)] focus:outline-none transition-colors"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50/60 p-4">
        <div className="flex items-end gap-4 flex-wrap">
          <FilterSelect
            label="Industry"
            value={industry}
            onChange={setIndustry}
            options={INDUSTRIES}
          />
          <FilterSelect
            label="Seniority"
            value={seniority}
            onChange={setSeniority}
            options={SENIORITY_LEVELS}
          />
          <FilterSelect
            label="Location"
            value={location}
            onChange={setLocation}
            options={LOCATIONS}
          />
          <FilterSelect
            label="Work Arrangement"
            value={workArrangement}
            onChange={setWorkArrangement}
            options={WORK_ARRANGEMENTS}
          />
          <CompletionRangeFilter
            minCompletion={minCompletion}
            maxCompletion={maxCompletion}
            onMinChange={handleMinCompletionChange}
            onMaxChange={handleMaxCompletionChange}
          />
          {activeFilterCount > 0 && (
            <button
              onClick={clearFilters}
              className="mb-0.5 rounded-lg px-3 py-2 text-small font-medium transition-colors"
              style={{ color: "var(--primary-main)" }}
            >
              Clear filters ({activeFilterCount})
            </button>
          )}
        </div>
      </div>

      {/* Results summary */}
      <div className="flex items-center justify-between mb-4">
        <SmallText className="text-gray-500">
          {filteredRoles.length} {filteredRoles.length === 1 ? "role" : "roles"} found
        </SmallText>
        <XsmallText className="text-gray-400">
          Sorted by interview completion
        </XsmallText>
      </div>

      {/* Grid */}
      {filteredRoles.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRoles.map((role) => (
            <RoleCard key={role.id} role={role} onSelect={setSelectedRole} />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
          <Heading5 className="text-gray-400 mb-2">No matching roles</Heading5>
          <SmallText className="text-gray-400">
            Try adjusting your search or filters.
          </SmallText>
        </div>
      )}

      <RoleDetailModal
        role={selectedRole}
        show={!!selectedRole}
        onClose={() => setSelectedRole(null)}
        saved={selectedRole ? savedRoles.has(selectedRole.id) : false}
        onToggleSave={handleToggleSave}
      />
    </div>
  );
}
